import React, { Component } from 'react';
import PropTypes from 'prop-types';
import adminpulse from '@/shared/utils/adminpulse';
import OutsideClickHandler from 'react-outside-click-handler';

const padNumber = value => {
  const numberValue = parseInt(value, 10) || 0;
  return numberValue < 10 ? `0${numberValue}` : `${numberValue}`;
};

class TimeInput extends Component {
  state = {
    hourEmpty: false,
    minuteEmpty: false,
    hasFocus: false,
    hourInput: '00',
    minuteInput: '00',
    lastKeyPressWasArrow: false,
  };

  onHoursKeyDown = e => {
    const { onKeyDown } = this.props;
    this.setState({ lastKeyPressWasArrow: e.keyCode === 38 || e.keyCode === 40 });

    if (onKeyDown) {
      onKeyDown(e);
    }
  };

  onHoursChanged = e => {
    const { padHours, id } = this.props;
    const { hourInput, minuteInput, lastKeyPressWasArrow } = this.state;
    let inputValue = e.target.value;
    const inputNumber = parseInt(inputValue, 10) || 0;
    // Changed from 00 to 0, so backspace or delete was used -> clear input
    if (hourInput === '00' && inputValue === '0') inputValue = '';
    // Changed from 02 to 3, so up or down button was used -> add padding
    else if (
      padHours &&
      hourInput !== '' &&
      inputValue !== '' &&
      Math.abs(inputNumber - parseInt(hourInput, 10)) === 1
    )
      inputValue = padNumber(inputValue);
    // Check if input values has to many leading zeros -> reset padding
    else if (inputValue.indexOf('0') === 0 && inputValue.length > 2)
      inputValue = padNumber(inputValue);

    this.onInputChanged(inputValue, minuteInput);

    if (
      // dont shift focus to minute field if the user is using the up/down arrow to increase/decrease hours. This results in bad UX
      !lastKeyPressWasArrow &&
      // only auto-advance when padHours is enabled (standard time entry with max 2-digit hours)
      padHours &&
      // use the element selector to get the amount of digits in the input field. The value of the number field is 9 even if the user entered 09.
      // after the 2nd digit, we jump to the minutes field
      document.getElementById(`${id}-HourField`).value.toString().length === 2
    ) {
      document.getElementById(`${id}-MinuteField`).focus();
    }
  };
  onMinuteChanged = e => {
    const { hourInput, minuteInput } = this.state;
    let inputValue = e.target.value;
    const inputNumber = parseInt(inputValue, 10) || 0;
    // Changed from 00 to 0, so backspace or delete was used -> clear input
    if (minuteInput === '00' && inputValue === '0') inputValue = '';
    // Changed from 02 to 3, so up or down button was used -> add padding
    else if (
      minuteInput !== '' &&
      inputValue !== '' &&
      Math.abs(inputNumber - parseInt(minuteInput, 10)) === 1
    )
      inputValue = padNumber(inputValue);
    // Check if input values has to many leading zeros -> reset padding
    else if (inputValue.indexOf('0') === 0 && inputValue.length > 2)
      inputValue = padNumber(inputValue);

    this.onInputChanged(hourInput, inputValue);
  };

  onInputChanged = (hour, minute) => {
    const { min, max, onChange, padHours } = this.props;
    let hourInput = hour;
    let minuteInput = minute;
    let validTime = null;
    if (hour !== '' && minute !== '') {
      const time = adminpulse.joinTime(parseInt(hour, 10), parseInt(minute, 10));
      validTime = Math.max(min, Math.min(max, time));
      if (time !== validTime) {
        // Change inputs to valid value
        const [validHour, validMinute] = adminpulse.splitTimeArray(validTime);
        hourInput = padHours ? padNumber(validHour) : `${validHour}`;
        minuteInput = padNumber(validMinute);
      }
    }
    this.setState({ hourInput, minuteInput }, () => {
      if (validTime !== null) {
        onChange(validTime);
      }
    });
  };

  handleFocus = () => {
    this.setState({ hasFocus: true });
  };

  // Submit on blur
  handleOutsideClick = () => {
    const { hasFocus, hourInput, minuteInput } = this.state;
    if (!hasFocus) return;
    const { onBlur, padHours } = this.props;
    const hour = padHours ? padNumber(hourInput) : `${hourInput || 0}`;
    const minute = padNumber(minuteInput);

    this.onInputChanged(hour, minute);
    this.setState({ hasFocus: false });
    onBlur();
  };
  handleClear = () => {
    const { onChange } = this.props;
    Promise.resolve(onChange(null)).then(() => {
      const { onBlur } = this.props;
      this.setState({ hasFocus: false });
      onBlur();
    });
  };

  resetInternalValue = () => {
    const { value, padHours } = this.props;
    this.setState(({ hourInput, minuteInput }) => {
      const newState = {};
      const [hour, minute] = adminpulse.splitTimeArray(value);
      const currentHour = parseInt(hourInput) || 0;
      const currentMinute = parseInt(minuteInput) || 0;

      if (hour === currentHour && minute === currentMinute) return undefined;
      if (hour !== currentHour) {
        newState.hourInput = padHours ? padNumber(hour) : `${hour}`;
      }

      if (minute !== currentMinute) {
        newState.minuteInput = padNumber(minute);
      }

      return newState;
    });
  };
  componentDidMount() {
    this.resetInternalValue();
  }
  componentDidUpdate(prevProps) {
    const { value, padHours } = this.props;
    if (prevProps.value !== value) {
      this.resetInternalValue();
    }
  }

  render() {
    const { disabled, id, autofocus, onKeyDown, allowNull, min, max } = this.props;
    const { hourInput, minuteInput } = this.state;
    let [minHour, minMinute] = adminpulse.splitTimeArray(min);
    let [maxHour, maxMinute] = adminpulse.splitTimeArray(max);
    const hourValue = parseInt(hourInput) || 0;
    if (hourValue > minHour) minMinute = -1;
    if (hourValue < maxHour) maxMinute = 60;

    return (
      <OutsideClickHandler onOutsideClick={this.handleOutsideClick}>
        <div className="input-group-sm time-input input-group" disabled={disabled}>
          <input
            data-1p-ignore
            id={`${id}-HourField`}
            name={`${id}-HourField`}
            type="number"
            step="1"
            disabled={disabled}
            value={hourInput}
            onFocus={this.handleFocus}
            onChange={this.onHoursChanged}
            onKeyDown={this.onHoursKeyDown}
            className="form-control"
            autoFocus={autofocus}
            min={minHour}
            max={maxHour}
          />
          <span className="input-group-addon">:</span>
          <input
            data-1p-ignore
            id={`${id}-MinuteField`}
            name={`${id}-MinuteField`}
            type="number"
            step="1"
            disabled={disabled}
            value={minuteInput}
            onKeyDown={onKeyDown}
            onFocus={this.handleFocus}
            onChange={this.onMinuteChanged}
            className="form-control"
            min={minMinute}
            max={maxMinute}
          />
          {allowNull ? (
            <span className="input-group-btn">
              <button className="btn btn-default" type="button" onClick={this.handleClear}>
                &times;
              </button>
            </span>
          ) : null}
        </div>
      </OutsideClickHandler>
    );
  }
}
TimeInput.propTypes = {
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  min: PropTypes.number,
  max: PropTypes.number,
  autofocus: PropTypes.bool,
  onKeyDown: PropTypes.func,
  allowNull: PropTypes.bool,
  padHours: PropTypes.bool,
};

TimeInput.defaultProps = {
  value: 0,
  disabled: false,
  id: 'TimeInput',
  onBlur: () => {},
  min: 0,
  max: 1439,
  autofocus: undefined,
  onKeyDown: undefined,
  allowNull: false,
  padHours: true,
};
export default TimeInput;
